<template>
  <div class="page-card">
    <h2 class="page-title">私聊管理</h2>

    <div class="toolbar">
      <div class="toolbar-left">
        <el-input
          v-model="query.keyword"
          clearable
          placeholder="搜索商家/艺人/内容"
          style="width: 280px"
          @keyup.enter.native="fetchData"
        />
        <el-select v-model="query.banned" clearable placeholder="封禁状态" style="width: 140px">
          <el-option :value="true" label="已封禁" />
          <el-option :value="false" label="未封禁" />
        </el-select>
        <el-button type="primary" @click="fetchData">查询</el-button>
      </div>
    </div>

    <el-table :data="list" border>
      <el-table-column prop="merchantName" label="商家" min-width="180" />
      <el-table-column prop="artistName" label="艺人" min-width="180" />
      <el-table-column prop="bizTypeText" label="来源类型" width="120" />
      <el-table-column prop="lastMessage" label="最后消息" min-width="260" show-overflow-tooltip />
      <el-table-column prop="lastMessageAt" label="最后时间" width="180" />
      <el-table-column label="状态" width="170">
        <template slot-scope="{ row }">
          <el-tag :type="row.banned ? 'danger' : 'success'">
            {{ row.banned ? (row.bannedRoleText || '已封禁') : '正常' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="340" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" @click="openDetail(row)">查看</el-button>
          <template v-if="!row.banned">
            <el-button type="text" @click="handleBan(row, 1)">封商家</el-button>
            <el-button type="text" @click="handleBan(row, 2)">封艺人</el-button>
            <el-button type="text" @click="handleBan(row, 3)">封双方</el-button>
          </template>
          <el-button v-else type="text" @click="handleUnban(row)">解封</el-button>
          <el-button type="text" @click="openSend(row)">平台发消息</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-drawer :visible.sync="detailVisible" title="私聊详情" size="48%">
      <div v-if="detail.session" class="detail-wrap">
        <div class="detail-head">
          <div>{{ detail.session.merchantName || '-' }} / {{ detail.session.artistName || '-' }}</div>
          <div>{{ detail.session.bizTypeText || '-' }}</div>
        </div>
        <div v-if="detail.session.banned" class="detail-ban">
          <el-tag type="danger">{{ detail.bannedRoleText || detail.session.bannedRoleText || '已封禁' }}</el-tag>
          <span>{{ detail.banReason || '-' }}</span>
        </div>
        <div class="message-list">
          <div v-for="item in detail.messages || []" :key="item.id" class="message-item">
            <div class="message-meta">
              <span>{{ senderLabel(item.senderRole) }}</span>
              <span>{{ item.sentAt || '-' }}</span>
            </div>
            <div class="message-content" :class="{ recalled: Number(item.isRecalled) === 1 }">
              {{ item.content || '-' }}
            </div>
          </div>
        </div>
      </div>
    </el-drawer>

    <el-dialog title="平台发送系统消息" :visible.sync="sendVisible" width="520px">
      <el-input
        v-model="sendForm.content"
        type="textarea"
        :rows="5"
        maxlength="300"
        show-word-limit
        placeholder="请输入平台系统消息"
      />
      <span slot="footer">
        <el-button @click="sendVisible = false">取消</el-button>
        <el-button type="primary" :loading="sending" @click="submitSend">发送</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import {
  banPrivateMessageSession,
  getPrivateMessageSessionDetail,
  getPrivateMessageSessions,
  sendPrivateMessageSystemNotice,
  unbanPrivateMessageSession
} from '../../../api/platformManage'

export default {
  name: 'PlatformPrivateMessagesPage',
  data() {
    return {
      query: {
        keyword: '',
        banned: undefined,
        merchantUserId: undefined,
        artistUserId: undefined
      },
      list: [],
      detailVisible: false,
      detail: {},
      sendVisible: false,
      currentSessionId: '',
      sending: false,
      sendForm: {
        content: ''
      }
    }
  },
  created() {
    this.applyRouteQuery()
    this.fetchData()
  },
  watch: {
    '$route.query': {
      handler() {
        this.applyRouteQuery()
        this.fetchData()
      }
    }
  },
  methods: {
    applyRouteQuery() {
      const routeQuery = this.$route.query || {}
      this.query.merchantUserId = routeQuery.merchantUserId ? Number(routeQuery.merchantUserId) : undefined
      this.query.artistUserId = routeQuery.artistUserId ? Number(routeQuery.artistUserId) : undefined
      if (routeQuery.keyword !== undefined) {
        this.query.keyword = routeQuery.keyword
      }
    },
    senderLabel(role) {
      if (Number(role) === 1) return '商家'
      if (Number(role) === 2) return '艺人'
      if (Number(role) === 9) return '平台'
      return '未知'
    },
    bannedRoleText(role) {
      if (Number(role) === 1) return '封禁商家私聊功能'
      if (Number(role) === 2) return '封禁艺人私聊功能'
      return '封禁双方私聊功能'
    },
    async fetchData() {
      this.list = await getPrivateMessageSessions(this.query).catch(() => [])
    },
    async openDetail(row) {
      this.detail = await getPrivateMessageSessionDetail(row.sessionId)
      this.detailVisible = true
    },
    async handleBan(row, bannedRole) {
      const prompt = await MessageBox.prompt(
        `请输入${this.bannedRoleText(bannedRole)}的原因`,
        '封禁私聊',
        {
          confirmButtonText: '确认',
          cancelButtonText: '取消',
          inputValidator: value => (value && value.trim() ? true : '请输入封禁原因')
        }
      ).catch(() => null)
      if (!prompt) return
      await banPrivateMessageSession(row.sessionId, {
        reason: prompt.value.trim(),
        bannedRole
      })
      this.$message.success('封禁成功')
      await this.fetchData()
      if (this.detailVisible && this.detail.session && this.detail.session.sessionId === row.sessionId) {
        this.detail = await getPrivateMessageSessionDetail(row.sessionId)
      }
    },
    async handleUnban(row) {
      await unbanPrivateMessageSession(row.sessionId)
      this.$message.success('解封成功')
      await this.fetchData()
      if (this.detailVisible && this.detail.session && this.detail.session.sessionId === row.sessionId) {
        this.detail = await getPrivateMessageSessionDetail(row.sessionId)
      }
    },
    openSend(row) {
      this.currentSessionId = row.sessionId
      this.sendForm.content = ''
      this.sendVisible = true
    },
    async submitSend() {
      const content = String(this.sendForm.content || '').trim()
      if (!content) {
        this.$message.warning('请输入消息内容')
        return
      }
      if (this.sending) {
        return
      }
      this.sending = true
      try {
        await sendPrivateMessageSystemNotice(this.currentSessionId, { content })
        this.$message.success('发送成功')
        this.sendVisible = false
        await this.fetchData()
        if (this.detailVisible && this.currentSessionId) {
          this.detail = await getPrivateMessageSessionDetail(this.currentSessionId)
        }
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style scoped>
.page-title {
  margin-bottom: 16px;
}

.detail-wrap {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-weight: 600;
  color: #303133;
}

.detail-ban {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #606266;
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message-item {
  padding: 12px;
  border-radius: 10px;
  background: #f7f8fa;
}

.message-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
  color: #909399;
  font-size: 12px;
}

.message-content {
  color: #303133;
  white-space: pre-wrap;
  word-break: break-word;
}

.message-content.recalled {
  color: #909399;
}
</style>
